<template>
	<Col :span="24">
		<VerticalLayout>
			<TypographyTitle :level="4">{{ $t('settings.databaseIndexes.header') }}</TypographyTitle>
			<TypographyParagraph>{{ $t('settings.databaseIndexes.summaryHint') }}</TypographyParagraph>
			<Card :bordered="false">
				<VerticalLayout>
					<div class="database-indexes-toolbar">
						<div class="database-indexes-toolbar__switch">
							<Switch v-model:checked="showDeleted" @change="reloadFromStart" />
							<span>{{ $t('settings.databaseIndexes.showDeleted') }}</span>
						</div>
						<div class="database-indexes-toolbar__field">
							<span>{{ $t('settings.databaseIndexes.database') }}</span>
							<Select
								v-model:value="databaseAliasFilter"
								:options="databaseAliasOptions"
								allow-clear
								class="database-indexes-toolbar__control"
								@change="reloadFromStart"
							/>
						</div>
						<div class="database-indexes-toolbar__field">
							<span>{{ $t('settings.databaseIndexes.checkMode') }}</span>
							<Select
								v-model:value="defaultCheckLevel"
								:options="checkLevelOptions"
								:loading="isSavingCheckLevel"
								class="database-indexes-toolbar__control"
								@change="onCheckLevelChange"
							/>
						</div>
						<div class="database-indexes-toolbar__action">
							<PopconfirmButton
								:title="$t('settings.databaseIndexes.resetAllChecks.confirmation')"
								:ok-text="$t('settings.databaseIndexes.resetAllChecks.ok')"
								:cancel-text="$t('settings.databaseIndexes.resetAllChecks.cancel')"
								:loading="isResettingAllChecks"
								:is-dangerous-operation="true"
								:disabled="isResettingAllChecks || resettingIndexId !== null"
								:onConfirm="resetAllChecks"
							>
								{{ $t('settings.databaseIndexes.resetAllChecks.label') }}
							</PopconfirmButton>
						</div>
					</div>
					<div class="scrollable-x">
						<Table
							:columns="columns"
							:data-source="tableRows"
							:loading="isLoading"
							:pagination="pagination"
							row-key="id"
							size="small"
							@change="onTableChange"
						>
							<template #bodyCell="{ column, record }">
								<template v-if="column.dataIndex === 'actions'">
									<Button size="small" :loading="resettingIndexId === record.id" :disabled="isResettingAllChecks" @click="resetChecks(record.id)">{{ $t('settings.databaseIndexes.resetChecks') }}</Button>
								</template>
								<template v-else-if="column.dataIndex === 'statsCollectedAtLabel'">
									<DateTimeDisplay :date="record.stats_collected_at" />
								</template>
								<template v-else-if="measurementColumnKeys.has(String(column.dataIndex))">
									<NoLineBreaks>{{ record[column.dataIndex] }}</NoLineBreaks>
								</template>
								<template v-else-if="record.checks[column.dataIndex]">
									<Tooltip v-if="record.checks[column.dataIndex].startedAt || record.checks[column.dataIndex].error">
										<template #title>
											<DateTimeDisplay v-if="record.checks[column.dataIndex].startedAt" :date="record.checks[column.dataIndex].startedAt" />
											<div v-if="record.checks[column.dataIndex].error">{{ record.checks[column.dataIndex].error }}</div>
										</template>
										<StatusDisplay :source="record.checks[column.dataIndex].status" :badgeDict="statusBadgeDict" prefix="settings.databaseIndexes.checkStatus" />
									</Tooltip>
									<StatusDisplay v-else :source="record.checks[column.dataIndex].status" :badgeDict="statusBadgeDict" prefix="settings.databaseIndexes.checkStatus" />
								</template>
								<template v-else-if="column.dataIndex === 'unresolved_integrity_error_at'">
									<Tooltip v-if="record.unresolved_integrity_error_message" :title="record.unresolved_integrity_error_message">
										<span>{{ $t('settings.databaseIndexes.unresolvedYes') }}</span>
									</Tooltip>
									<span v-else>-</span>
								</template>
							</template>
						</Table>
					</div>
				</VerticalLayout>
			</Card>
		</VerticalLayout>
	</Col>
</template>

<script lang="ts">
import { ref, computed, onMounted, defineComponent } from 'vue';
import { Col, Card, Table, Select, Switch, TypographyTitle, TypographyParagraph, Tooltip, Button } from 'ant-design-vue';
import type { TableProps } from 'ant-design-vue/es/table';

import ApiDatabaseIndexes from '@/api/ApiDatabaseIndexes';
import Consts from '@/api/Consts';
import type { SelectOption } from '@/components/common/types/selectOptionTypes';
import type {
	DatabaseIndexCheckLevel,
	DatabaseIndexesQueryParams,
	DatabaseIndexesResponse,
	IndexHealthLevelCheck,
	IndexHealthMonitoredIndexItem,
} from '@/api/ApiDatabaseIndexes';
import { useI18n } from 'vue-i18n';

import DateTimeDisplay from '@/components/common/DateTimeDisplay.vue';
import NoLineBreaks from '@/components/common/NoLineBreaks.vue';
import StatusDisplay from '@/components/common/StatusDisplay.vue';
import VerticalLayout from '@/components/common/VerticalLayout.vue';
import PopconfirmButton from '@/components/common/PopconfirmButton.vue';

const measurementColumnKeys = new Set([
	'sizeLabel', 'idxScanLabel', 'leafDensityLabel', 'leafFragmentationLabel',
	'fastDurationLabel', 'extendedDurationLabel', 'deepDurationLabel',
]);

type LevelStatusCell = {
	status: string;
	startedAt: string | null;
	error: string | null;
};

const statusBadgeDict = {
	never: 'default',
	ok: 'success',
	timeout: 'warning',
	error: 'error',
	skip: 'default',
};

type DatabaseIndexTableRow = IndexHealthMonitoredIndexItem & {
	sizeLabel: string;
	idxScanLabel: string;
	leafDensityLabel: string;
	leafFragmentationLabel: string;
	catalogFlagsLabel: string;
	checks: Record<string, LevelStatusCell>;
	fastDurationLabel: string;
	extendedDurationLabel: string;
	deepDurationLabel: string;
};

const formatSizeLabel = (bytes: number | null | undefined): string => {
	if (bytes == null) {
		return '-';
	}
	const units = ['B', 'KB', 'MB', 'GB', 'TB'];
	let value = bytes;
	let unitIndex = 0;
	while (value >= 1024 && unitIndex < units.length - 1) {
		value /= 1024;
		unitIndex += 1;
	}
	return value.toFixed(2) + ' ' + (units[unitIndex] ?? 'B');
};

const formatDurationLabel = (durationMs: number | null): string => {
	if (durationMs == null) {
		return '-';
	}
	return String(durationMs) + ' ms';
};

const formatIndexScanLabel = (count: number | null): string => {
	return count == null ? '-' : count.toLocaleString();
};

const formatPercentLabel = (value: number | null | undefined): string => {
	return value == null ? '-' : String(value) + ' %';
};

const formatCatalogFlagsLabel = (record: IndexHealthMonitoredIndexItem): string => {
	const parts = [];
	if (!record.is_valid) {
		parts.push('invalid');
	}
	if (!record.is_ready) {
		parts.push('not ready');
	}
	if (!record.is_live) {
		parts.push('not live');
	}
	if (record.is_unique) {
		parts.push('unique');
	}
	if (record.is_primary) {
		parts.push('primary');
	}
	return parts.length ? parts.join(', ') : '-';
};

export default defineComponent({
	components: {
		Col,
		Card,
		Table,
		Select,
		Switch,
		TypographyTitle,
		TypographyParagraph,
		Tooltip,
		Button,
		DateTimeDisplay,
		NoLineBreaks,
		StatusDisplay,
		VerticalLayout,
		PopconfirmButton,
	},
	setup() {
		const { t } = useI18n();
		const isLoading = ref(false);
		const isSavingCheckLevel = ref(false);
		const isResettingAllChecks = ref(false);
		const resettingIndexId = ref<number | null>(null);
		const items = ref<IndexHealthMonitoredIndexItem[]>([]);
		const summary = ref<DatabaseIndexesResponse['summary'] | null>(null);
		const columns = Consts.databaseIndexesTableColumnsComp;
		const showDeleted = ref(false);
		const databaseAliasFilter = ref<string | undefined>(undefined);
		const defaultCheckLevel = ref<DatabaseIndexCheckLevel>('deep');
		const page = ref(1);
		const pageSize = ref(50);
		const total = ref(0);
		const sortField = ref<string | null>(null);
		const sortDir = ref<'asc' | 'desc'>('asc');

		const databaseAliasOptions = computed<SelectOption<string>[]>(() => [
			{ label: 'default', value: 'default' },
			{ label: 'rinex', value: 'rinex' },
		]);

		const checkLevelOptions = computed<SelectOption<DatabaseIndexCheckLevel>[]>(() => [
			{ label: t('settings.databaseIndexes.tableColumns.fastStatus'), value: 'fast' },
			{ label: t('settings.databaseIndexes.tableColumns.extendedStatus'), value: 'extended' },
			{ label: t('settings.databaseIndexes.tableColumns.deepStatus'), value: 'deep' },
		]);

		const levelStatusCell = (level: IndexHealthLevelCheck): LevelStatusCell => {
			return {
				status: level.display_status,
				startedAt: level.started_at,
				error: level.error,
			};
		};

		const tableRows = computed(() => items.value.map(row => ({
			...row,
			sizeLabel: formatSizeLabel(row.size_bytes),
			idxScanLabel: formatIndexScanLabel(row.idx_scan),
			leafDensityLabel: formatPercentLabel(row.index_stats.avg_leaf_density),
			leafFragmentationLabel: formatPercentLabel(row.index_stats.leaf_fragmentation),
			catalogFlagsLabel: formatCatalogFlagsLabel(row),
			checks: {
				fastStatus: levelStatusCell(row.fast),
				extendedStatus: levelStatusCell(row.extended),
				deepStatus: levelStatusCell(row.deep),
			},
			fastDurationLabel: formatDurationLabel(row.fast.duration_ms),
			extendedDurationLabel: formatDurationLabel(row.extended.duration_ms),
			deepDurationLabel: formatDurationLabel(row.deep.duration_ms),
		})));

		const pagination = computed(() => ({
			current: page.value,
			pageSize: pageSize.value,
			total: total.value,
			showSizeChanger: true,
			pageSizeOptions: ['25', '50', '100'],
		}));

		const fetchData = () => {
			isLoading.value = true;
			const params: DatabaseIndexesQueryParams = {
				page: page.value,
				page_size: pageSize.value,
				presence: showDeleted.value ? 'deleted' : 'present',
			};
			if (databaseAliasFilter.value) {
				params.database_alias = databaseAliasFilter.value;
			}
			if (sortField.value) {
				params.sort = sortField.value;
				params.sort_dir = sortDir.value;
			}
			ApiDatabaseIndexes.utilsDatabaseIndexesGet(params, response => {
				if (response.status === 'ok') {
					items.value = response.items || [];
					total.value = response.count || 0;
					summary.value = response.summary || null;
					if (response.default_check_level) {
						defaultCheckLevel.value = response.default_check_level;
					}
				}
				isLoading.value = false;
			});
		};

		const onCheckLevelChange = (value: DatabaseIndexCheckLevel) => {
			if (!value || isSavingCheckLevel.value) {
				return;
			}
			isSavingCheckLevel.value = true;
			ApiDatabaseIndexes.utilsDatabaseIndexDefaultLevelSet(value, response => {
				isSavingCheckLevel.value = false;
				if (response.status === 'ok' && response.default_check_level) {
					defaultCheckLevel.value = response.default_check_level;
				}
			});
		};

		const reloadFromStart = () => {
			page.value = 1;
			fetchData();
		};

		const resetChecks = (indexId: number) => {
			if (resettingIndexId.value !== null || isResettingAllChecks.value) {
				return;
			}
			resettingIndexId.value = indexId;
			ApiDatabaseIndexes.utilsDatabaseIndexChecksReset(indexId, response => {
				resettingIndexId.value = null;
				if (response.status === 'ok') {
					fetchData();
				}
			});
		};

		const resetAllChecks = () => {
			if (isResettingAllChecks.value || resettingIndexId.value !== null) {
				return;
			}
			isResettingAllChecks.value = true;
			ApiDatabaseIndexes.utilsDatabaseIndexChecksResetAll(response => {
				isResettingAllChecks.value = false;
				if (response.status === 'ok') {
					fetchData();
				}
			});
		};

		const sortFieldMap: Record<string, string> = {
			fastStatus: 'fast_status',
			extendedStatus: 'extended_status',
			deepStatus: 'deep_status',
			fastDurationLabel: 'fast_duration_ms',
			extendedDurationLabel: 'extended_duration_ms',
			deepDurationLabel: 'deep_duration_ms',
			sizeLabel: 'size_bytes',
			idxScanLabel: 'idx_scan',
		};

		const onTableChange: NonNullable<TableProps<DatabaseIndexTableRow>['onChange']> = (
			paginationConfig,
			_filters,
			sorter,
		) => {
			page.value = paginationConfig.current || 1;
			pageSize.value = paginationConfig.pageSize || pageSize.value;
			const currentSorter = Array.isArray(sorter) ? sorter[0] : sorter;
			if (currentSorter?.order && currentSorter.field != null) {
				const field = String(currentSorter.field);
				sortField.value = sortFieldMap[field] || field;
				sortDir.value = currentSorter.order === 'descend' ? 'desc' : 'asc';
			} else if (currentSorter?.field != null) {
				sortField.value = null;
			}
			fetchData();
		};

		onMounted(() => {
			fetchData();
		});

		return {
			isLoading,
			isResettingAllChecks,
			resettingIndexId,
			resetChecks,
			resetAllChecks,
			items,
			summary,
			columns,
			measurementColumnKeys,
			statusBadgeDict,
			showDeleted,
			databaseAliasFilter,
			databaseAliasOptions,
			defaultCheckLevel,
			checkLevelOptions,
			isSavingCheckLevel,
			onCheckLevelChange,
			tableRows,
			pagination,
			reloadFromStart,
			onTableChange,
		};
	},
});
</script>

<style scoped>
.database-indexes-toolbar {
	display: flex;
	flex-direction: column;
	align-items: stretch;
	gap: 12px;
}

.database-indexes-toolbar__field {
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.database-indexes-toolbar__control {
	width: 100%;
}

.database-indexes-toolbar__switch {
	display: flex;
	align-items: center;
	gap: 8px;
}

@media (min-width: 768px) {
	.database-indexes-toolbar {
		flex-direction: row;
		flex-wrap: wrap;
		align-items: flex-end;
		gap: 16px 24px;
	}

	.database-indexes-toolbar__field {
		flex: 0 0 220px;
		width: 220px;
	}

	.database-indexes-toolbar__switch,
	.database-indexes-toolbar__action {
		display: flex;
		align-items: center;
		height: 32px;
	}
}
</style>
